import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { X, Check, XCircle, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const statusOpts = ["awaiting_payment", "verifying", "paid", "delivered", "failed", "cancelled"];
const statusStyle = {
  awaiting_payment: "bg-yellow-500/15 text-yellow-400 border-yellow-500/25",
  verifying: "bg-blue-500/15 text-blue-400 border-blue-500/25",
  paid: "bg-emerald-500/15 text-emerald-400 border-emerald-500/25",
  delivered: "bg-emerald-500/15 text-emerald-400 border-emerald-500/25",
  failed: "bg-red-500/15 text-red-400 border-red-500/25",
  cancelled: "bg-white/5 text-kyro-mute border-white/10",
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);

  const save = async (patch = {}) => {
    try {
      const body = {
        status: patch.status ?? editing.status,
        delivery_notes: patch.delivery_notes ?? editing.delivery_notes,
        credentials_delivered: patch.credentials_delivered ?? editing.credentials_delivered,
      };
      await api.put(`/admin/orders/${editing.id}`, body);
      toast.success("Order updated");
      setEditing(null); load();
    } catch { toast.error("Save failed"); }
  };

  const approve = async () => save({ status: "paid" });
  const reject = async () => save({ status: "failed" });

  const filtered = orders.filter((o) => {
    if (filter !== "all" && o.status !== filter) return false;
    if (q && !o.id.includes(q) && !(o.guest_email || "").includes(q) && !(o.payment_utr || "").includes(q)) return false;
    return true;
  });

  const counts = statusOpts.reduce((acc, s) => ({ ...acc, [s]: orders.filter((o) => o.status === s).length }), {});

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <div className="kyro-overline">Fulfillment</div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter mt-2">Orders</h1>
        </div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ID / email / UTR..." className="bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm outline-none w-72" data-testid="admin-order-search" />
      </div>

      {/* Filter chips */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-4">
        <button onClick={() => setFilter("all")} className={`shrink-0 px-3 py-1.5 rounded-full text-xs border ${filter === "all" ? "bg-[#FF4433] text-white border-[#FF4433]" : "border-white/10 hover:bg-white/5"}`}>All ({orders.length})</button>
        {statusOpts.map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`filter-orders-${s}`} className={`shrink-0 px-3 py-1.5 rounded-full text-xs border ${filter === s ? "bg-[#FF4433] text-white border-[#FF4433]" : "border-white/10 hover:bg-white/5"}`}>
            {s.replace("_", " ")} ({counts[s] || 0})
          </button>
        ))}
      </div>

      <div className="kyro-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5">
            <tr className="text-left">
              <th className="px-4 py-3 font-medium text-kyro-mute">Order</th>
              <th className="px-4 py-3 font-medium text-kyro-mute">Customer</th>
              <th className="px-4 py-3 font-medium text-kyro-mute">Total</th>
              <th className="px-4 py-3 font-medium text-kyro-mute">UTR</th>
              <th className="px-4 py-3 font-medium text-kyro-mute">Status</th>
              <th className="px-4 py-3 font-medium text-kyro-mute w-0"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan="6" className="px-4 py-12 text-center text-kyro-mute">No orders here yet.</td></tr>
            )}
            {filtered.map((o) => (
              <tr key={o.id} className="border-t border-white/5" data-testid={`admin-order-row-${o.id}`}>
                <td className="px-4 py-3">
                  <p className="font-mono text-xs">{o.id}</p>
                  <p className="text-xs text-kyro-mute">{new Date(o.created_at).toLocaleString()}</p>
                </td>
                <td className="px-4 py-3">
                  <p className="text-sm">{o.guest_name || "—"}</p>
                  <p className="text-xs text-kyro-mute">{o.guest_email}</p>
                </td>
                <td className="px-4 py-3 font-semibold">₹{o.total.toFixed(0)}</td>
                <td className="px-4 py-3">
                  {o.payment_utr ? (
                    <span className="font-mono text-xs">{o.payment_utr}</span>
                  ) : (
                    <span className="text-xs text-kyro-faint">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${statusStyle[o.status] || ""}`}>{o.status.replace("_", " ")}</span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <button onClick={() => setEditing({ ...o })} className="kyro-btn-secondary text-xs" data-testid={`manage-order-${o.id}`}>Manage</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm grid place-items-start p-4 overflow-y-auto">
          <div className="kyro-card w-full max-w-3xl p-6 md:p-8 my-8 mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="text-xs text-kyro-mute">Order</div>
                <p className="font-mono text-sm">{editing.id}</p>
              </div>
              <button onClick={() => setEditing(null)} className="w-9 h-9 grid place-items-center rounded-full hover:bg-white/5"><X className="w-4 h-4" /></button>
            </div>

            {/* Payment section */}
            {editing.status === "verifying" && (
              <div className="p-4 rounded-xl border border-blue-500/30 bg-blue-500/5 mb-5">
                <div className="text-[10px] font-bold uppercase tracking-widest text-blue-400 mb-3">Awaiting your verification</div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <div className="text-[10px] text-kyro-mute uppercase tracking-widest">UTR</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="font-mono text-sm">{editing.payment_utr}</span>
                      <button onClick={() => { navigator.clipboard.writeText(editing.payment_utr); toast.success("UTR copied"); }} className="w-6 h-6 grid place-items-center rounded hover:bg-white/5"><Copy className="w-3 h-3" /></button>
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] text-kyro-mute uppercase tracking-widest">Amount</div>
                    <div className="font-display font-bold text-lg mt-1">₹{editing.total.toFixed(0)}</div>
                  </div>
                  {editing.payment_note && (
                    <div className="md:col-span-2">
                      <div className="text-[10px] text-kyro-mute uppercase tracking-widest">Customer note</div>
                      <p className="text-sm mt-1">{editing.payment_note}</p>
                    </div>
                  )}
                  {editing.payment_proof_url && (
                    <div className="md:col-span-2">
                      <div className="text-[10px] text-kyro-mute uppercase tracking-widest mb-2">Payment screenshot</div>
                      <a href={editing.payment_proof_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-[#FF4433] hover:underline">
                        <img src={editing.payment_proof_url} alt="proof" className="w-24 h-24 object-cover rounded-lg border border-white/10" />
                        <span className="inline-flex items-center gap-1">Open full <ExternalLink className="w-3 h-3" /></span>
                      </a>
                    </div>
                  )}
                </div>
                <div className="flex gap-2 mt-4">
                  <button onClick={approve} className="kyro-btn-primary text-sm inline-flex items-center gap-2" data-testid="approve-payment-btn"><Check className="w-4 h-4" /> Approve & mark paid</button>
                  <button onClick={reject} className="kyro-btn-secondary text-sm inline-flex items-center gap-2 text-red-400" data-testid="reject-payment-btn"><XCircle className="w-4 h-4" /> Reject</button>
                </div>
              </div>
            )}

            {editing.payment_utr && editing.status !== "verifying" && (
              <div className="p-4 rounded-xl border border-white/5 bg-white/5 mb-5 text-sm">
                <div className="text-[10px] text-kyro-mute uppercase tracking-widest mb-1">UTR</div>
                <span className="font-mono">{editing.payment_utr}</span>
              </div>
            )}

            <div className="mb-4">
              <div className="kyro-overline mb-2">Items</div>
              {editing.items.map((it, i) => (
                <div key={i} className="flex items-center justify-between py-1 text-sm">
                  <span>{it.product_name} — {it.plan_label}</span>
                  <span>₹{it.price.toFixed(0)}</span>
                </div>
              ))}
              <div className="flex items-center justify-between text-sm mt-2 pt-2 border-t border-white/5">
                <strong>Total</strong>
                <strong>₹{editing.total.toFixed(0)}</strong>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="text-xs text-kyro-mute uppercase tracking-widest">Status</label>
                <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 outline-none" data-testid="edit-order-status">
                  {statusOpts.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-kyro-mute uppercase tracking-widest">Delivered?</label>
                <div className="mt-1">
                  <label className="flex items-center gap-2 text-sm py-2">
                    <input type="checkbox" checked={!!editing.credentials_delivered} onChange={(e) => setEditing({ ...editing, credentials_delivered: e.target.checked })} className="accent-[#FF4433]" />
                    Credentials delivered
                  </label>
                </div>
              </div>
            </div>
            <div>
              <label className="text-xs text-kyro-mute uppercase tracking-widest">Delivery notes (shown to customer)</label>
              <textarea rows={6} value={editing.delivery_notes || ""} onChange={(e) => setEditing({ ...editing, delivery_notes: e.target.value })} className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 outline-none font-mono text-sm" placeholder="email: xxxx@xxx.com&#10;password: xxxxx&#10;valid until: 2026-08-01" data-testid="edit-order-notes" />
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="kyro-btn-secondary text-sm">Cancel</button>
              <button onClick={() => save()} className="kyro-btn-primary text-sm" data-testid="save-order-btn">Save changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
